import { destroyCookie, parseCookies, setCookie } from 'nookies'
import { SUPPORT_COOKIE, TOKEN_COOKIE } from '@/constants/session'
import { whileSameSession } from '@/services/core/sessionGuard'
import { clearAllStores } from '@/stores/clearAllStores'
import { useAuthStore } from '@/stores/useAuthStore'
import { useCartStore } from '@/stores/useCartStore'
import { AuthSessionSchema, IAuthSession } from '~/schemas/auth/authSchemas'
import { api, apiService, getTokenMaxAgeSeconds, SESSION_EXPIRED_MESSAGE } from '~/services/core/ApiService'

/*
 * Sessão do cliente na loja. O cookie `@glofi:token` decide se há sessão (é o que o proxy e o ApiService usam); a
 * useAuthStore é o cache do cliente. O cookie `@glofi:support` marca o acesso do atendente (login-as-customer).
 */

const cookieOptions = (maxAge: number) => ({ maxAge, httpOnly: false, path: '/' })

export const getSessionToken = (): string | undefined => parseCookies()[TOKEN_COOKIE] || undefined

export const isSupportSession = (): boolean => Boolean(parseCookies()[SUPPORT_COOKIE])

/** Claims de um JWT (o payload), sem validar a assinatura: quem valida é a API. Malformado devolve `null`. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
    const parts = token.split('.')
    if (parts.length !== 3 || !parts[1]) return null
    try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
        const binary = atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='))
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
        const claims: unknown = JSON.parse(new TextDecoder().decode(bytes))
        return claims && typeof claims === 'object' && !Array.isArray(claims)
            ? (claims as Record<string, unknown>)
            : null
    } catch {
        return null
    }
}

/**
 * Id do cliente na claim `nameid` do token da sessão (o `customer` que os logins devolvem não tem id). Sem token, com
 * token malformado ou sem a claim, `null`.
 */
export function getSessionCustomerId(token: string | null | undefined): string | null {
    const nameid = token ? decodeJwtPayload(token)?.nameid : undefined
    return typeof nameid === 'string' && nameid ? nameid : null
}

/**
 * Outro cliente já estava logado no navegador (login social ou link de outro cliente): token anterior diferente do
 * novo e, quando os dois trazem o id (`nameid`), de outro cliente. Sem token anterior (sessão anônima), não é troca.
 */
function isAnotherCustomer(previousToken: string | null | undefined, nextToken: string): boolean {
    if (!previousToken || previousToken === nextToken) return false
    const previousId = getSessionCustomerId(previousToken)
    const nextId = getSessionCustomerId(nextToken)
    return !previousId || !nextId || previousId !== nextId
}

/** Carrinho da sessão na store do carrinho (o mesmo `CartSchema` do domínio cart). */
function applyCart(session: IAuthSession) {
    if (session.cart) useCartStore.getState().setCart(session.cart)
}

// =========================================================================
// SECTION 1: ABRIR E ENCERRAR
// =========================================================================

/**
 * Abre a sessão devolvida por um login ou cadastro: token no cookie, cliente na useAuthStore e carrinho na store do
 * carrinho. Com `supportExpiration` (segundos, do login-as-customer), token e marca de suporte valem só esse tempo;
 * sem ele, a marca de suporte de um acesso anterior é apagada. Se havia outra sessão de cliente (token diferente), as
 * stores e o cache do React Query do cliente anterior são limpos antes (`clearAllStores`); vindo da sessão anônima,
 * carrinho e cupom guardados ficam.
 */
export function startSessionService(session: IAuthSession, options: { supportExpiration?: number } = {}): void {
    const { supportExpiration } = options

    if (isAnotherCustomer(getSessionToken() ?? useAuthStore.getState().data?.token, session.token)) clearAllStores()

    if (supportExpiration) {
        setCookie(undefined, SUPPORT_COOKIE, 'true', cookieOptions(supportExpiration))
        setCookie(undefined, TOKEN_COOKIE, session.token, cookieOptions(supportExpiration))
    } else {
        destroyCookie(undefined, SUPPORT_COOKIE, { path: '/' })
        setCookie(undefined, TOKEN_COOKIE, session.token, cookieOptions(getTokenMaxAgeSeconds()))
    }

    useAuthStore.getState().setAuthData(supportExpiration ? { ...session, supportAccess: true } : session)
    applyCart(session)
}

/** Encerra a sessão (logout): apaga token e marca de suporte e limpa todas as stores. A sessão anônima fica. */
export function endSessionService(): void {
    clearAllStores()
    destroyCookie(undefined, TOKEN_COOKIE, { path: '/' })
    destroyCookie(undefined, SUPPORT_COOKIE, { path: '/' })
}

/**
 * Sessão que não vale mais (update-token 400, cache sem token): encerra e avisa a aplicação pelo mesmo evento
 * `sessionExpired` do interceptor de 401, que mostra o aviso e manda para o login.
 */
export function expireSessionService(message: string = SESSION_EXPIRED_MESSAGE): void {
    endSessionService()
    window.dispatchEvent(new CustomEvent('sessionExpired', { detail: { type: 'sessionExpired', message } }))
}

// =========================================================================
// SECTION 2: RENOVAR (POST auth/update-token)
// =========================================================================

/**
 * Grava o token devolvido pelo `update-token`. No acesso de suporte, mantém o token de suporte: o `update-token` emite
 * um token normal, que transformaria a sessão curta do atendente numa sessão de 14 dias como o cliente.
 */
function saveRefreshedToken(token: string): void {
    if (isSupportSession()) return
    setCookie(undefined, TOKEN_COOKIE, token, cookieOptions(getTokenMaxAgeSeconds()))
}

/**
 * Renova a sessão pelo `auth/update-token` (token novo, cliente e carrinho), usado para recompor o cache e depois de
 * editar dados ou endereço. Devolve `null` quando um login ou logout aconteceu durante a chamada (a resposta é
 * descartada). No acesso de suporte o token do atendente é mantido (`saveRefreshedToken`). Erros sem aviso padrão: 400
 * é sessão sem cliente, quem chama decide (ver `useSessionRefresh`).
 */
export async function refreshSessionService(): Promise<IAuthSession | null> {
    let applied = false

    const session = await apiService(api)
        .post('auth/update-token', undefined, AuthSessionSchema, { meta: { showLoader: true } })
        .onSuccess(
            whileSameSession((data) => {
                saveRefreshedToken(data.token)
                useAuthStore.getState().setAuthData(isSupportSession() ? { ...data, supportAccess: true } : data)
                applyCart(data)
                applied = true
            }),
        )
        .onError(() => {}, { useDefaultErrorHandler: false })
        .execute()

    return applied ? session : null
}

// =========================================================================
// SECTION 3: CONFERIR A SESSÃO
// =========================================================================

export type SessionAction = 'none' | 'rehydrate' | 'signOut' | 'expire'

export interface SessionState {
    hasToken: boolean
    hasCache: boolean
    isSupport: boolean
}

/**
 * O que fazer com a sessão: o cookie `@glofi:token` decide se o cliente está logado (é o que o middleware usa) e o store
 * de autenticação é só um cache. Token sem cache recompõe pelo `update-token`, exceto no acesso de suporte (o
 * `update-token` emitiria um token normal no lugar do de suporte); cache sem token é sessão expirada.
 */
export function reconcileSession({ hasToken, hasCache, isSupport }: SessionState): SessionAction {
    if (hasToken && !hasCache) return isSupport ? 'signOut' : 'rehydrate'
    if (!hasToken && hasCache) return 'expire'
    return 'none'
}

// =========================================================================
// SECTION 4: DESTINO DEPOIS DO LOGIN
// =========================================================================

/** Origem fictícia para resolver o destino: só importa que o resultado continue nela (no servidor não há `window`). */
const STORE_ORIGIN = 'https://loja.invalid'
/** Caractere de controle (o parser de URL os remove: `/\t/site` vira `//site`) ou barra invertida (vira `/`). */
const hasUnsafePathChar = (path: string) =>
    Array.from(path).some((char) => {
        const code = char.charCodeAt(0)
        return code < 0x20 || code === 0x7f || char === '\\'
    })

/**
 * Destino interno da loja (`/perfil`, `/pedido/1?pix=1`), resolvido como o navegador resolveria: caminho absoluto, sem
 * caractere de controle nem barra invertida, que continua na mesma origem. Devolve caminho, query e fragmento
 * resolvidos, ou `null` para outro site (`//site`, `/\t/site`, `https:`, `javascript:`). O `?redirect=` vem da URL
 * (já decodificado pelo `URLSearchParams`) e chega aqui sem filtro.
 */
function toInternalPath(path: string): string | null {
    if (!path.startsWith('/') || path.startsWith('//') || hasUnsafePathChar(path)) return null
    try {
        const url = new URL(path, STORE_ORIGIN)
        return url.origin === STORE_ORIGIN ? `${url.pathname}${url.search}${url.hash}` : null
    } catch {
        return null
    }
}

/**
 * Destino depois de entrar: o pedido pela tela (o `?redirect=` do proxy), se for um caminho da loja; sem ele, o
 * checkout se o carrinho tem itens, senão a home.
 */
export function getPostLoginPath(session: IAuthSession, redirect?: string | null): string {
    const internal = redirect ? toInternalPath(redirect) : null
    if (internal) return internal
    return session.cart && session.cart.cartItems.length > 0 ? '/checkout' : '/'
}
